import { css, SerializedStyles } from '@emotion/react';
import { times } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CardAffinity } from '@mythos/game/CardTypes';
import { FAVOR_PER_TRIBUTE_TOKEN } from '@mythos/game/GameConstants';
import { EventTypes, GameEventOfType } from '@mythos/game/GameEvents';
import {
  GamePlayerViewModel,
  GameViewModel,
  updateGameViewModel,
} from '@mythos/game/GameViewModel';
import { Phase } from '@mythos/game/Phases';
import { Resource } from '@mythos/game/Resources';
import * as Rules from '@mythos/game/Rules';
import { Bid, CardWithID } from '@mythos/game/Rules';
import { getTributeLogic } from '@mythos/game/TributeLogic';
import {
  TutorialHighlightElementGlobalBoard,
  TutorialHighlightElementPlayerSummary,
  TutorialHighlightType,
  TutorialTooltip,
} from '@mythos/game/Tutorial';
import { CounterDelta } from '@mythos/game/Utility';
import Session from '@mythos/utils/Session';
import { RecursiveItemOrArray } from '@mythos/utils/ts_utils';
import { addToMapOfArrays, makeStringID, nonNull } from '@mythos/utils/utils';
import { useMobile } from 'MobileContext';
import invariant from 'invariant';
import nullthrows from 'nullthrows';
import { match } from 'ts-pattern';
import ActionStore, { useActionStoreListener } from './ActionStore';
import { AnimationRefSetter, PlayerAnimationRefs } from './AnimationHooks';
import AnimationSystem, {
  AddAnimationParams,
  AnimationLayer,
  ElementPosition,
  NodeTarget,
  PointPosition,
} from './AnimationSystem';
import {
  CardRowAnimations,
  CountersExplosionAnimation,
  DelayAnimation,
  DiscardCardAnimation,
  GainCardAnimation,
  MakePlayerIncrementors,
  tokenExplosionAnimations,
} from './Animations';
import { CARD_BORDER_RADIUS } from './BaseCardView';
import { CardBackType } from './CardBackView';
import { SymbolWithNumber } from './CardHelpers';
import { BasicCardRenderer } from './CardView';
import CollapsibleGameLinksView from './CollapsibleGameLinksView';
import { PhaseToBackgroundColor, UIColorPalette } from './ColorPalette';
import {
  ConflictResultsDisplay,
  ConflictResultsDisplayRef,
  ResolutionPhaseConflictResultsDisplay,
} from './ConflictResultsDisplay';
import { DeckView, TributeDeckView } from './DeckView';
import { EmptyCard } from './EmptyCardView';
import EndOfGameView from './EndOfGameView';
import GameStateView from './GameStateView';
import { UserWithColor } from './GraphQLHelpers';
import HoverCardStore from './HoverCardStore';
import { LogView2 } from './LogView2';
import { Modal } from './Modal';
import PileView, {
  DeckCard,
  PileCard,
  PileCardRenderer,
  PileTributeCard,
  PileTributeCardRenderer,
} from './PileView';
import PilesContainer from './PilesContainerView';
import PlayerBoardView from './PlayerBoardView';
import PlayerSummaryView from './PlayerSummaryView';
import SelfPlayerView from './SelfPlayerView';
import Symbols, { SymbolView } from './Symbols';
import TributeCardView, {
  MakeHighlightedAgeTributeRenderer,
} from './TributeCardView';
import {
  PlayerSummarySectionHighlightStyle,
  TUTORIAL_HIGHLIGHT_COLOR,
  TutorialMessage,
} from './TutorialViews';
import { getUserPrefs, useUserPref } from './UserPreferences';
import MGameView from './mobile_ui/MGameView';
import { MEDIA_QUERY_MOBILE } from './mobile_ui/MStyles';

import { isCardMercenary } from '@mythos/game/Utility';
import { CardSelectorView } from './CardSelectorView';

interface PlayerResourceProps {
  game: GameViewModel;
  players: GamePlayerViewModel[];
  sessionPlayerID?: string;
  userByID: Map<string, UserWithColor>;
  readyByUserID: { [k: string]: boolean };
  animationRefSetter?: AnimationRefSetter;
  containerStyles?: {
    favor?: SerializedStyles;
    resources?: SerializedStyles;
    typeCounts?: SerializedStyles;
  };
}
const PlayerResourceViews = (props: PlayerResourceProps) => {
  let {
    game,
    players,
    sessionPlayerID,
    userByID,
    readyByUserID,
    animationRefSetter,
  } = props;

  const [countMode, setCountMode] = useUserPref(
    'playerSummaryCountMode',
    'counters',
  );
  const [favorMode, setFavorMode] = useUserPref(
    'playerSummaryFavorMode',
    'total',
  );
  const [cardCountTheme, setCardCountTheme] = useUserPref(
    'cardCountTheme',
    'default',
  );

  let session_player_index = players.findIndex((player) => {
    return player.userID === sessionPlayerID;
  });
  let sorted_players = players;
  if (session_player_index !== -1) {
    let left = sorted_players.slice(session_player_index);
    let right = sorted_players.slice(0, session_player_index);
    sorted_players = left.concat(right);
  }

  const favor_by_player_id = new Map(
    players.map((player) => {
      const context = Rules.makeContextFromGame(player, game);
      const { cardFavor, tributeFavor, prophecyFavor } =
        Rules.computePlayerFavorDetails(player, context);
      return [player.userID, cardFavor + tributeFavor + prophecyFavor];
    }),
  );

  const max_total_favor = Math.max(...Array.from(favor_by_player_id.values()));
  let views = sorted_players.map((player) => {
    const playerTotalFavor = favor_by_player_id.get(player.userID)!;
    return (
      <PlayerSummaryView
        key={player.userID}
        game={game}
        player={player}
        user={userByID.get(player.userID)!}
        isReady={readyByUserID[player.userID]}
        isWinning={max_total_favor === playerTotalFavor && max_total_favor > 0}
        countMode={countMode}
        animationRefSetter={animationRefSetter}
        toggleCountMode={() => {
          setCountMode(countMode === 'total' ? 'counters' : 'total');
        }}
        favorMode={favorMode}
        toggleFavorMode={() => {
          setFavorMode(favorMode === 'total' ? 'detailed' : 'total');
        }}
        cardCountTheme={cardCountTheme}
        toggleCardCountTheme={() => {
          setCardCountTheme(
            cardCountTheme === 'colored_bg' ? 'default' : 'colored_bg',
          );
        }}
        containerStyles={props.containerStyles}
      />
    );
  });
  return <div css={PlayerResourceStyles.container}>{views}</div>;
};
const PlayerResourceStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  }),
};

function get_conflict_notes(
  game: GameViewModel,
  userByID: Map<string, UserWithColor>,
): {
  [cardID: string]: React.ReactNode;
} {
  let events: GameEventOfType<EventTypes.CONFLICT_RESULTS>[] = [];
  let seenCardIDs = new Set<string>();
  for (let i = game.events.length - 1; i >= 0; i--) {
    const event = game.events[i];
    if (
      event.type === EventTypes.SET_UP_AGE ||
      event.type === EventTypes.SET_UP_TURN
    ) {
      break;
    }
    if (event.type !== EventTypes.CONFLICT_RESULTS) {
      continue;
    }
    if (seenCardIDs.has(event.payload.cardID)) {
      continue;
    }
    seenCardIDs.add(event.payload.cardID);

    events.push(event);
  }

  if (events.length === 0) {
    return {};
  }

  return Object.fromEntries(
    events.map((event) => {
      invariant(
        event.type === EventTypes.CONFLICT_RESULTS,
        'expected conflict',
      );
      const cardID = event.payload.cardID;

      const el = event.payload.winningPlayerID ? (
        <ResolutionPhaseConflictResultsDisplay
          event={event}
          userByID={userByID}
        />
      ) : (
        <div css={GameStyles.cardOverlay}>
          <ConflictResultsDisplay
            rounds={event.payload.rounds}
            winningPlayerID={event.payload.winningPlayerID}
            userByID={userByID}
            onCompleted={() => {}}
            animate={false}
          />
        </div>
      );

      return [cardID, <div css={GameStyles.cardOverlay}>{el}</div>];
    }),
  );
}

const CARD_DECK_ID = 'deck';
const TRIBUTE_DECK_ID = 'deck-tribute';

export function GlobalAreaView(props: {
  game: GameViewModel;
  actionStore?: ActionStore;
  userByID: Map<string, UserWithColor>;
  selfPlayerView: React.ReactNode;

  onDraftingDeckClick?: () => void;
  onTributeDeckClick?: () => void;

  cardIDToAnimation?: Map<string, HTMLDivElement>;
  cardIndexToAnimation?: Map<number, HTMLDivElement>;
  tributeRowIndexToAnimation?: Map<number, HTMLDivElement>;

  currentTutorialMessage?: TutorialTooltip | null;
}) {
  let { actionStore, game, userByID, currentTutorialMessage } = props;
  const isMobile = useMobile();
  const onDraftingCardClick = useCallback(
    (card: PileCard) => {
      if (card.id === CARD_DECK_ID) {
        props.onDraftingDeckClick?.();
        return;
      }
      actionStore && actionStore.didClickDraftRowCard(card as CardWithID);
    },
    [actionStore, props.onDraftingDeckClick],
  );
  const onTributeCardClick = useCallback(
    (card: PileTributeCard) => {
      if (card.id === TRIBUTE_DECK_ID) {
        props.onTributeDeckClick?.();
      }
    },
    [actionStore, props.onTributeDeckClick],
  );
  const [modalContent, setModalContent] = useState<React.ReactElement | null>(
    null,
  );

  const mercenaryCards = game.basicPile.filter((card) => isCardMercenary(card));
  const firstMercenaryCardDefID = mercenaryCards[0]?.defID ?? '';
  const [selectedMercenaryDefID, setSelectedMercenaryDefID] = useState<string>(
    firstMercenaryCardDefID,
  );
  const [mercenaryExplicitlySelected, setMercenaryExplicitlySelected] =
    useState(false);
  useEffect(() => {
    setSelectedMercenaryDefID(firstMercenaryCardDefID);
    setMercenaryExplicitlySelected(false);
  }, [game.age, game.turn, firstMercenaryCardDefID]);

  const basicCardsToRender = game.basicPile.filter((card) => {
    return !isCardMercenary(card) || card.defID === selectedMercenaryDefID;
  });

  const backgroundColor = PhaseToBackgroundColor[game.phase];

  let overlayByCardID: { [cardID: string]: React.ReactNode } = {};
  const addOverlay = (cardID: string, overlay: React.ReactNode) => {
    const existing = overlayByCardID[cardID];
    overlayByCardID[cardID] = existing ? (
      <>
        {existing}
        {overlay}
      </>
    ) : (
      overlay
    );
  };

  actionStore?.getDisallowedCardIDs().forEach((id) =>
    addOverlay(
      id,
      <div
        css={GameStyles.cardOverlay}
        style={{
          backgroundColor: UIColorPalette.disallowedCardOverlay,
        }}
      />,
    ),
  );

  addOverlay(
    CARD_DECK_ID,
    <div css={GameStyles.cardOverlay}>ASDF {game.deck.length}</div>,
  );
  addOverlay(
    TRIBUTE_DECK_ID,
    <div css={GameStyles.cardOverlay}>{game.tributeDeck.length}</div>,
  );

  if (game.phase === Phase.PLANNING || game.phase === Phase.RESOLUTION) {
    actionStore?.getUnaffordableCards().forEach((reason, id) => {
      const overlay = (
        <div css={GameStyles.cardOverlay}>
          <div css={GameStyles.unaffordableCardText}>{reason}</div>
        </div>
      );
      addOverlay(id, overlay);
    });
  }
  if (game.phase === Phase.RESOLUTION || game.phase === Phase.WAR) {
    Object.entries(get_conflict_notes(game, userByID)).forEach(
      ([cardID, overlay]) => addOverlay(cardID, overlay),
    );
  }

  const noteRenderer = (props: any) => {
    let final_props = { ...props };

    let overlay = overlayByCardID[props.card.id];
    if (overlay) {
      final_props.overlayChildren = overlay;
    }

    if (isCardMercenary(props.card) && !mercenaryExplicitlySelected) {
      final_props.overrides = {
        genericCost: true,
        genericGainSelf: 'mercenary',
      };
    }

    // XXX: Use full size cards for now
    // final_props.mini = isMobile;

    return PileCardRenderer(final_props);
  };

  const modalNoteRenderer = (props: any) => {
    let final_props = { ...props };

    let overlay = overlayByCardID[props.card.id];
    if (overlay) {
      final_props.overlayChildren = overlay;
    }

    return PileCardRenderer(final_props);
  };

  const onBasicCardClick = useCallback(
    (card: CardWithID) => {
      if (isCardMercenary(card) && game.phase === Phase.RESOLUTION) {
        setModalContent(
          <CardSelectorView
            title="Select an Option:"
            cards={game.basicPile.filter((c) => isCardMercenary(c))}
            renderer={modalNoteRenderer}
            onSelectCard={(selectedCard) => {
              if (actionStore) {
                actionStore.didClickBasicCard(selectedCard);
                if (
                  Rules.canAffordCard(
                    Rules.makeContextFromGame(actionStore.getPlayer(), game),
                    selectedCard,
                  ).canAfford
                ) {
                  setSelectedMercenaryDefID(selectedCard.defID);
                  setMercenaryExplicitlySelected(true);
                  setModalContent(null);
                }
              }
            }}
          />,
        );
      } else if (isCardMercenary(card) && game.phase === Phase.PLANNING) {
        // Show mercenaries for reference only during Planning — no selection
        setModalContent(
          <CardSelectorView
            title="Mercenary Options:"
            cards={game.basicPile.filter((c) => isCardMercenary(c))}
            renderer={modalNoteRenderer}
            onSelectCard={() => setModalContent(null)}
          />,
        );
      } else {
        actionStore && actionStore.didClickBasicCard(card);
      }
    },
    [actionStore, game.basicPile, game.phase],
  );

  const tributeAgeRenderer = (props: any) => {
    props.currentAge = game.age;
    return PileTributeCardRenderer(props);
  };
  const tributeRenderer = (props: any) => {
    return tributeAgeRenderer({ ...props, mini: isMobile });
  };

  var highlight_color_by_card_id: { [k: string]: string } = {};
  if (actionStore) {
    actionStore.getCompletedTributeIDs().forEach((id) => {
      highlight_color_by_card_id[id] = UIColorPalette.completedTributeHighlight;
    });
    actionStore.getTutorialHighlightCardIDs().forEach((id) => {
      highlight_color_by_card_id[id] = TUTORIAL_HIGHLIGHT_COLOR;
    });

    const selectedCardID = actionStore.getSelectedCardID();
    if (selectedCardID) {
      const canAfford = actionStore.canAffordCard(
        game.cardsByID[selectedCardID],
      );
      highlight_color_by_card_id[selectedCardID] = canAfford.canAfford
        ? UIColorPalette.selectedCardHighlight
        : 'red';
    }
  }

  const cardBack = useMemo<DeckCard>(() => {
    return {
      type: `age${game.age}` as CardBackType,
      id: CARD_DECK_ID,
    };
  }, [game.age]);
  const tributeBack = useMemo<DeckCard>(() => {
    return { type: `tribute` as CardBackType, id: TRIBUTE_DECK_ID };
  }, []);

  const tradeRow: PileCard[] = [
    ...(isMobile ? [] : [cardBack]),
    ...times<EmptyCard>(game.boardSize - game.table.length, (i) => ({
      id: `empty${i}`,
    })),
    ...game.table,
  ];
  const tributeRow: PileTributeCard[] = [
    ...(isMobile ? [] : [tributeBack]),
    ...times<EmptyCard>(game.tributeRowSize - game.tributeRow.length, (i) => ({
      id: `emptytribute${i}`,
    })),
    ...game.tributeRow,
  ];

  const globalPileGlowStyle = {
    boxShadow: `0 0 10px 5px ${TUTORIAL_HIGHLIGHT_COLOR}`,
    borderRadius: CARD_BORDER_RADIUS,
  };
  let tributesTutorialGlowStyle = undefined;
  let tradeRowTutorialGlowStyle = undefined;
  let basicCardsTutorialGlowStyle = undefined;
  if (
    currentTutorialMessage?.highlightType === TutorialHighlightType.GLOBAL_BOARD
  ) {
    currentTutorialMessage.highlightedElements.forEach((e) => {
      match(e)
        .with(TutorialHighlightElementGlobalBoard.TRIBUTES, () => {
          tributesTutorialGlowStyle = globalPileGlowStyle;
        })
        .with(TutorialHighlightElementGlobalBoard.TRADE_ROW, () => {
          tradeRowTutorialGlowStyle = globalPileGlowStyle;
        })
        .with(TutorialHighlightElementGlobalBoard.BASIC_CARDS, () => {
          basicCardsTutorialGlowStyle = globalPileGlowStyle;
        })
        .otherwise(() => {});
    });
  }

  return (
    <div
      css={GlobalAreaStyles.container}
      style={{
        backgroundColor,
      }}
    >
      <div css={GlobalAreaStyles.pilesContainer}>
        <PilesContainer title="Basic Cards">
          <PileView
            key="Basic Cards"
            css={GlobalAreaStyles.pile}
            style={basicCardsTutorialGlowStyle}
            cards={basicCardsToRender}
            onCardClick={onBasicCardClick}
            renderer={noteRenderer}
            hoverRenderer={BasicCardRenderer}
            highlightColorByCardID={highlight_color_by_card_id}
            cardIDToAnimation={props.cardIDToAnimation}
          />
        </PilesContainer>
        <PilesContainer title="Trade Row">
          <PileView
            key="Trade Row"
            css={GlobalAreaStyles.pile}
            style={tradeRowTutorialGlowStyle}
            cards={tradeRow}
            onCardClick={onDraftingCardClick}
            renderer={noteRenderer}
            hoverRenderer={PileCardRenderer}
            highlightColorByCardID={highlight_color_by_card_id}
            cardIDToAnimation={props.cardIDToAnimation}
            cardIndexToAnimation={props.cardIndexToAnimation}
          />
          {props.selfPlayerView}
        </PilesContainer>
        <PilesContainer
          title={
            <div css={GameStyles.tributeRowTitle}>
              Tributes (
              <SymbolView
                symbol={Symbols.TOKEN_TRIBUTE}
                css={GameStyles.tributeTitleSymbol}
              />
              =
              <SymbolWithNumber
                height={12}
                symbol={Symbols.FAVOR}
                number={FAVOR_PER_TRIBUTE_TOKEN}
              />
              )
            </div>
          }
        >
          <PileView
            key="Tributes"
            css={GlobalAreaStyles.pile}
            style={tributesTutorialGlowStyle}
            cards={tributeRow}
            onCardClick={onTributeCardClick}
            renderer={tributeRenderer}
            hoverRenderer={tributeAgeRenderer}
            highlightColorByCardID={highlight_color_by_card_id}
            cardIDToAnimation={props.cardIDToAnimation}
            cardIndexToAnimation={props.tributeRowIndexToAnimation}
          />
        </PilesContainer>
      </div>
      <Modal show={!!modalContent} onHide={() => setModalContent(null)}>
        {modalContent ?? undefined}
      </Modal>
    </div>
  );
}
const GlobalAreaStyles = {
  container: css({
    display: 'block',
    margin: 5,
    marginTop: 0,
    marginBottom: 0,
    paddingRight: 2,
    userSelect: 'none',

    transition: 'background-color 0.5s',

    [MEDIA_QUERY_MOBILE]: {
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: 4,
    },

    flexGrow: 1,
  }),
  pilesContainer: css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap-reverse',
    justifyContent: 'center',
    alignItems: 'flex-end',

    [MEDIA_QUERY_MOBILE]: {
      flexDirection: 'column-reverse',
      flexWrap: 'nowrap',
      gap: 6,
    },
  }),
  pile: css({
    [MEDIA_QUERY_MOBILE]: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
    },
  }),
};

interface Props {
  game: GameViewModel;
  userByID: Map<string, UserWithColor>;
  actionStore?: ActionStore;
  session?: Session;
}

export default function GameView(props: Props) {
  let { game: incomingGame, userByID, actionStore } = props;

  const cardIDToAnimationRef = useRef(new Map<string, HTMLDivElement>());
  const tradeRowIndexToAnimationRef = useRef(new Map<number, HTMLDivElement>());
  const tributeRowIndexToAnimationRef = useRef(
    new Map<number, HTMLDivElement>(),
  );
  const userIDToAnimationRefs = useRef(
    new Map<string, Map<PlayerAnimationRefs, HTMLDivElement>>(),
  );
  const animationsNeedNotifyRef = useRef<boolean>(false);
  const animationRefSetter: AnimationRefSetter = (playerID, type, ref) => {
    if (ref) {
      if (!userIDToAnimationRefs.current.has(playerID)) {
        userIDToAnimationRefs.current.set(playerID, new Map());
      }
      userIDToAnimationRefs.current.get(playerID)!.set(type, ref);
    } else {
      userIDToAnimationRefs.current.get(playerID)?.delete(type);
    }
  };

  const isMobile = useMobile();
  const processedEventCountRef = useRef<number>(incomingGame.events.length);
  const referenceGameRef = useRef<GameViewModel>(incomingGame);
  const animatingKeyRef = useRef<string | null>(null);
  const holdAnimationRef = useRef<boolean>(false);

  const forceUpdate = React.useReducer((x) => x + 1, 0)[1];

  actionStore && useActionStoreListener(actionStore);

  const useAnimations = !isMobile && !getUserPrefs().disableAnimations;

  let referenceGame = referenceGameRef.current;
  if (animatingKeyRef.current) {
  } else if (
    useAnimations &&
    processedEventCountRef.current !== incomingGame.events.length
  ) {
    invariant(
      incomingGame.events.length >= referenceGame.events.length,
      'Events went backwards',
    );
    const newEvents = incomingGame.events.slice(processedEventCountRef.current);

    const COMBINE_PHASES = false;
    const COMBINE_ANIMATION_PHASES = new Set<Phase>(
      COMBINE_PHASES ? [Phase.TRIBUTE, Phase.SETUP] : [],
    );

    const tributeCardIDToGainingPlayerIDs = new Map<string, string[]>();
    const cardAnimationData: {
      playerID: string;

      gameCard: CardWithID;
      reward: Partial<CounterDelta>;
      goldCost: number;
      prophecyFavor: number;
    }[] = [];
    const productionAnimationData: {
      playerID: string;
      counters: Partial<CounterDelta>;
    }[] = [];
    let gainedCardIDs = new Set<string>();
    let conflictInfo: {
      playerIDToBid: Map<string, Bid>;
      conflictResults: GameEventOfType<EventTypes.CONFLICT_RESULTS>[];
    } | null = null;
    let addedTableCardIDs: string[] = [];
    let removedTableCardIDs = new Set<string>();
    let addedTributeCardIDs: string[] = [];
    let removedTributeCardIDs = new Set<string>();

    // Track gold costs from PURCHASE_CARD events to use in GAIN_CARD animations
    const purchaseGoldCostByCardID = new Map<string, number>();

    let nextProcessedEventCount = incomingGame.events.length;
    for (let i = 0; i < newEvents.length; i++) {
      const event = newEvents[i];
      if (event.type === EventTypes.PURCHASE_CARD) {
        const cardID = event.payload.cardID;
        const goldCost = event.payload.spentCounters?.gold || 0;
        purchaseGoldCostByCardID.set(cardID, goldCost);
      } else if (event.type === EventTypes.GAIN_CARD) {
        const userID = nullthrows(event.payload.userID);
        let cardID = nullthrows(event.payload.cardID);
        gainedCardIDs.add(cardID);
        let gameCard = incomingGame.cardsByID[cardID] as CardWithID;
        if (gameCard.affinity === CardAffinity.Basic) {
          const basicCard = referenceGame.basicPile.find(
            (card) => card.defID === gameCard.defID,
          );
          if (basicCard) {
            gameCard = basicCard;
            cardID = basicCard.id;
          }
        }
        const reward = event.payload.cardCounters;
        const gold_cost =
          purchaseGoldCostByCardID.get(event.payload.cardID) || 0;
        // XXX(ldg): broken, read prophecyFavor from triggeredCards
        // const prophecy_favor = event.payload.prophecyFavor || 0;
        const prophecy_favor = 0;

        cardAnimationData.push({
          playerID: userID,
          gameCard,
          reward,
          goldCost: gold_cost,
          prophecyFavor: prophecy_favor,
        });
        removedTableCardIDs.add(event.payload.cardID);
      } else if (event.type === EventTypes.GAIN_TRIBUTES) {
        const userID = nullthrows(event.payload.userID);
        const tributeIDs = event.payload.tributeIDs!;
        tributeIDs.forEach((cardID) => {
          addToMapOfArrays(tributeCardIDToGainingPlayerIDs, cardID, userID);
          removedTributeCardIDs.add(cardID);
        });
      } else if (event.type === EventTypes.SET_UP_PHASE) {
        if (COMBINE_ANIMATION_PHASES.has(event.payload.phase)) {
          continue;
        }
        nextProcessedEventCount = processedEventCountRef.current + i + 1;
        console.log(
          'breaking on finish phase',
          event.payload.phase,
          nextProcessedEventCount,
          incomingGame.events.length,
        );
        break;
      } else if (event.type === EventTypes.END_OF_PHASE) {
        // nop
      } else if (event.type === EventTypes.DISCARD_CARD) {
        removedTableCardIDs.add(event.payload.cardID);
      } else if (event.type === EventTypes.DEAL_CARD) {
        addedTableCardIDs.push(event.payload.cardID);
      } else if (event.type === EventTypes.DEAL_TRIBUTE) {
        addedTributeCardIDs.push(event.payload.cardID);
      } else if (event.type === EventTypes.DISCARD_TRIBUTE) {
        removedTributeCardIDs.add(event.payload.cardID);
      } else if (event.type === EventTypes.CONFLICT_RESULTS) {
        if (!conflictInfo) {
          conflictInfo = {
            playerIDToBid: new Map(
              incomingGame.playerStates.map((p) => [p.userID, p.bid!]),
            ),
            conflictResults: [],
          };
        }
        conflictInfo.conflictResults.push(event);
      } else if (event.type === EventTypes.PRODUCTION) {
        const userID = event.payload.userID;
        productionAnimationData.push({
          playerID: userID,
          counters: event.payload.counters,
        });
      }
    }

    if (referenceGame.events.length < processedEventCountRef.current) {
      referenceGame = updateGameViewModel(
        referenceGame,
        incomingGame.events.slice(
          referenceGame.events.length,
          processedEventCountRef.current,
        ),
      );
      referenceGameRef.current = referenceGame;
    }

    processedEventCountRef.current = nextProcessedEventCount;

    let animationKey = makeStringID();
    const animations: AddAnimationParams[] = [];
    let blockingAnimation:
      | RecursiveItemOrArray<AddAnimationParams>
      | undefined = undefined;

    let playerIDToResourceIncrementors = new Map<
      string,
      Map<Resource, (delta: number) => void>
    >();
    if (
      cardAnimationData.length > 0 ||
      tributeCardIDToGainingPlayerIDs.size > 0 ||
      productionAnimationData.length > 0 ||
      conflictInfo
    ) {
      referenceGame.playerStates.forEach((player) => {
        const userID = player.userID;
        const [incrementors, anims] = MakePlayerIncrementors({
          player,
          playerAnimationRefs: userIDToAnimationRefs.current.get(userID)!,
          game: referenceGame,
        });
        playerIDToResourceIncrementors.set(userID, incrementors);
        animations.push(...anims);
      });
    }

    if (cardAnimationData.length > 0) {
      cardAnimationData.sort((a, b) => {
        let aScore = incomingGame.playerStates.findIndex(
          (player) => player.userID === a.playerID,
        );
        let bScore = incomingGame.playerStates.findIndex(
          (player) => player.userID === b.playerID,
        );

        return aScore - bScore;
      });

      cardAnimationData.forEach((data) => {
        const userID = data.playerID;
        const gameCard = data.gameCard;
        const reward = data.reward;
        const goldCost = data.goldCost;
        const prophecyFavor = data.prophecyFavor;
        const ret = GainCardAnimation({
          card: gameCard,
          reward,
          goldCost,
          prophecyFavor,
          cardIDToAnimationRef: cardIDToAnimationRef.current,
          playerAnimationRefs: userIDToAnimationRefs.current.get(userID)!,
          playerResourceIncrementors:
            playerIDToResourceIncrementors.get(userID)!,

          delayUntilFinished: blockingAnimation,
        });
        if (ret) {
          animations.push(...ret.animations);
          blockingAnimation = ret.blockingAnimation;
        }
      });
    }

    if (tributeCardIDToGainingPlayerIDs.size > 0) {
      referenceGame.tributeRow.forEach((card) => {
        const cardID = card.id;
        const playerIDs = tributeCardIDToGainingPlayerIDs.get(cardID);
        if (!playerIDs || playerIDs.length === 0) {
          return;
        }
        let startCard = cardIDToAnimationRef.current.get(cardID);
        if (!startCard) {
          console.warn('No start card for', cardID);
          return;
        }

        const discardBP = DiscardCardAnimation({
          node: <TributeCardView card={card} />,
          cardElement: startCard,

          delayUntilFinished: blockingAnimation,
        });
        if (discardBP) {
          animations.push(...discardBP.animations);
        }

        const tributeLogic = nullthrows(getTributeLogic(card));

        let delay = 0;
        playerIDs.forEach((userID) => {
          const context = Rules.makeContextFromGame(
            referenceGame.playerStates.find((p) => p.userID === userID)!,
            referenceGame,
          );
          const counterDelta = tributeLogic.onGainCounterDelta(context);
          const favor = counterDelta.favor;

          animations.push(
            ...tokenExplosionAnimations({
              symbol: Symbols.FAVOR,
              count: favor,

              startElement: ElementPosition(startCard, {
                anchorPoint: [0.5, 0.5],
              }),
              endElement: ElementPosition(
                userIDToAnimationRefs.current
                  .get(userID)
                  ?.get(PlayerAnimationRefs.favor)!,
              ),
              delayUntilFinished: blockingAnimation,
              delaySeconds: delay,

              style: 'l_explosion',

              incrementor: playerIDToResourceIncrementors
                .get(userID)
                ?.get('favor')!,
            }),
          );
          delay += 0.1;
        });
        blockingAnimation = discardBP.blockingAnimation || blockingAnimation;
      });
    }

    if (conflictInfo) {
      conflictInfo.playerIDToBid.forEach((bid, playerID) => {
        const bidAmount = bid.military;
        const playerAnimationRefs =
          userIDToAnimationRefs.current.get(playerID)!;

        const militaryView = playerAnimationRefs.get(
          PlayerAnimationRefs.military,
        )!;
        const startElement = ElementPosition(militaryView);
        const endElement = ElementPosition(
          nullthrows(
            cardIDToAnimationRef.current.get(
              referenceGame.table[bid.tradeRowIndex].id,
            ),
          ),
        );

        if (bidAmount > 0) {
          const incrementor = playerIDToResourceIncrementors
            .get(playerID)!
            .get('military')!;
          animations.push(
            ...tokenExplosionAnimations({
              symbol: Symbols.MILITARY,
              count: bidAmount,

              startElement,
              endElement,

              delayUntilFinished: blockingAnimation,
              style: 'train',
              incrementor: (count) => incrementor(-count),
            }),
          );
        }
      });

      holdAnimationRef.current = true;

      let conflictAnimations: AddAnimationParams[] = [];
      let currentResultIndex = 0;
      let resultsRefs: Map<number, ConflictResultsDisplayRef> = new Map();
      // filter for only latest reroll round for each card
      let seenCardIDs = new Set<string>();
      const events = [...conflictInfo.conflictResults]
        .reverse()
        .filter((event) => {
          if (seenCardIDs.has(event.payload.cardID)) {
            return false;
          }
          seenCardIDs.add(event.payload.cardID);
          return true;
        });

      events.forEach((event, i) => {
        const cardID = event.payload.cardID;

        const cardElement = cardIDToAnimationRef.current.get(cardID);
        if (!cardElement) {
          console.warn('No card element for', cardID);
          return;
        }

        holdAnimationRef.current = true;
        const conflictAnimation: AddAnimationParams = {
          target: NodeTarget(
            <ConflictResultsDisplay
              rounds={event.payload.rounds}
              winningPlayerID={event.payload.winningPlayerID}
              userByID={userByID}
              animate={true}
              onCompleted={() => {
                currentResultIndex++;
                if (currentResultIndex == events.length) {
                  setTimeout(() => {
                    if (animatingKeyRef.current === animationKey) {
                      holdAnimationRef.current = false;
                      AnimationSystem.clearAnimations();
                    }
                  }, 500);
                } else if (currentResultIndex < events.length) {
                  resultsRefs.get(currentResultIndex)?.startAnimation();
                } else {
                  invariant(false, 'unexpected');
                }
              }}
              ref={(ref) => {
                if (ref) {
                  resultsRefs.set(i, ref);
                  if (i === currentResultIndex) {
                    ref.startAnimation();
                  }
                } else {
                  resultsRefs.delete(i);
                }
              }}
            />,
            { anchorPoint: [0.5, 0.5] },
          ),
          startElement: ElementPosition(cardElement, {
            anchorPoint: [0.5, 0.5],
          }),

          delayUntilFinished: blockingAnimation,
          durationSeconds: 1,
          finishPauseSeconds: 15,
        };

        animations.push(conflictAnimation);
        conflictAnimations.push(conflictAnimation);
      });
      if (conflictAnimations.length > 0) {
        animations.push(
          DelayAnimation({
            delaySeconds: 0.1,
            delayUntilFinished: conflictAnimations,
          }),
        );
        blockingAnimation = animations[animations.length - 1];
      }
    }

    let cardRowBlockingAnimations: AddAnimationParams[] = [];
    if (
      cardAnimationData.length > 0 ||
      removedTableCardIDs.size > 0 ||
      addedTableCardIDs.length > 0
    ) {
      const bp = CardRowAnimations({
        cardIDsToAdd: addedTableCardIDs,
        cardIDsToRemove: removedTableCardIDs,
        alreadyRemovedCardIDs: new Set(
          cardAnimationData.map((x) => x.gameCard.id),
        ),
        cardIDToAnimationRef: cardIDToAnimationRef.current,
        rowIndexToAnimationRef: tradeRowIndexToAnimationRef.current,
        row: referenceGame.table,
        rowSize: referenceGame.boardSize,
        cardsByID: incomingGame.cardsByID,
        cardRenderer: (card) => BasicCardRenderer({ card }),

        delayUntilFinished: blockingAnimation,
      });
      if (bp) {
        animations.push(...bp.animations);
        cardRowBlockingAnimations.push(bp.blockingAnimation);
      }
    }

    if (
      addedTributeCardIDs.length > 0 ||
      removedTributeCardIDs.size > 0 ||
      tributeCardIDToGainingPlayerIDs.size > 0
    ) {
      const tributeRenderer = MakeHighlightedAgeTributeRenderer(
        referenceGame.age,
      );
      const bp = CardRowAnimations({
        cardIDsToAdd: addedTributeCardIDs,
        cardIDsToRemove: removedTributeCardIDs,
        alreadyRemovedCardIDs: new Set(tributeCardIDToGainingPlayerIDs.keys()),
        cardIDToAnimationRef: cardIDToAnimationRef.current,
        rowIndexToAnimationRef: tributeRowIndexToAnimationRef.current,
        row: referenceGame.tributeRow,
        rowSize: referenceGame.tributeRowSize,
        cardsByID: incomingGame.tributeCardsByID,
        cardRenderer: (card) => tributeRenderer({ card }),

        delayUntilFinished: blockingAnimation,
      });
      if (bp) {
        animations.push(...bp.animations);
        cardRowBlockingAnimations.push(bp.blockingAnimation);
      }
    }
    blockingAnimation = cardRowBlockingAnimations;

    if (productionAnimationData.length > 0) {
      let productionAnimations = productionAnimationData.map((data) => {
        const userID = data.playerID;

        const ret = CountersExplosionAnimation({
          counters: data.counters,
          playerAnimationRefs: userIDToAnimationRefs.current.get(userID)!,
          playerResourceIncrementors:
            playerIDToResourceIncrementors.get(userID)!,

          style: 'l_explosion',

          delayUntilFinished: blockingAnimation,
        });
        if (ret) {
          animations.push(...ret.animations);
        }
        return ret?.blockingAnimation;
      });
      let filteredProductionAnimations = productionAnimations.filter(nonNull);
      if (filteredProductionAnimations.length > 0) {
        blockingAnimation = filteredProductionAnimations;
      }
    }

    if (animations.length > 0) {
      animations.push({
        target: null,
        startElement: PointPosition([0, 0]),
        delayUntilFinished: blockingAnimation,
        durationSeconds: 0.25,
      });

      console.log('submitting animations', animations.length);
      AnimationSystem.addAnimations(animations);
      animationsNeedNotifyRef.current = true;
      animatingKeyRef.current = animationKey;
    } else {
      console.log('new events, no animations, moving to new game');
      // XXX: this should recurse before setting the incoming game
      // referenceGameRef.current = incomingGame;
      forceUpdate();
    }
  } else {
    // XXX get rid of this cloneDeep, need to deal with mutable data as an implication
    referenceGameRef.current = incomingGame;
    processedEventCountRef.current = incomingGame.events.length;
  }
  const game = referenceGameRef.current;
  console.log('rendering game', game.phase);

  useEffect(() => {
    if (animationsNeedNotifyRef.current) {
      // AnimationSystem_.addAnimations(animationsToAddRef.current);
      AnimationSystem._notifyListeners();
      animationsNeedNotifyRef.current = false;
    }
  });
  const onAnimationComplete = useCallback(() => {
    if (
      animatingKeyRef.current &&
      !AnimationSystem.hasAnimations() &&
      !holdAnimationRef.current
    ) {
      animatingKeyRef.current = null;
      forceUpdate();
    }
  }, [animatingKeyRef, holdAnimationRef, forceUpdate]);
  useEffect(() => {
    const listener = onAnimationComplete;
    AnimationSystem.addListener(listener);
    return () => {
      AnimationSystem.removeListener(listener);
    };
  }, [onAnimationComplete]);

  const [modalContent, setModalContent] = useState<React.ReactElement | null>(
    null,
  );

  let sessionUserID = actionStore && actionStore.getPlayer().userID;
  let session_player: GamePlayerViewModel | null = null;
  let sortedPlayers: GamePlayerViewModel[] = [];
  for (let player of game.playerStates) {
    if (player.userID === sessionUserID) {
      session_player = player;
      sortedPlayers.unshift(player);
    } else {
      sortedPlayers.push(player);
    }
  }

  let self_player_view: React.ReactNode = null;
  if (session_player && !game.gameEndTimestamp) {
    sessionUserID = session_player.userID;
    self_player_view = (
      <SelfPlayerView
        actionStore={actionStore!}
        phase={game.phase}
        ready={game.readyByUserID[sessionUserID]}
        rolls={undefined}
        waitingForAnimation={!!animatingKeyRef.current}
        onSkipAnimation={() => {
          if (animatingKeyRef.current) {
            holdAnimationRef.current = false;
            AnimationSystem.clearAnimations();

            referenceGameRef.current = incomingGame;
            processedEventCountRef.current = incomingGame.events.length;
          }
        }}
      />
    );
  }

  if (isMobile) {
    // TODO end of game view
    return (
      <MGameView
        game={game}
        userByID={userByID}
        actionStore={actionStore}
        sessionUserID={sessionUserID}
      />
    );
  }

  const currentTutorialMessage = !animatingKeyRef.current
    ? actionStore?.getTutorialMessage()
    : null;

  let game_content: React.ReactNode = null;
  if (game.gameEndTimestamp) {
    game_content = <EndOfGameView game={game} userByID={userByID} />;
  } else {
    game_content = (
      <>
        <GameStateView game={game} />
        <GlobalAreaView
          actionStore={actionStore}
          game={game}
          userByID={userByID}
          selfPlayerView={self_player_view}
          cardIDToAnimation={cardIDToAnimationRef.current}
          cardIndexToAnimation={tradeRowIndexToAnimationRef.current}
          tributeRowIndexToAnimation={tributeRowIndexToAnimationRef.current}
          onDraftingDeckClick={() => {
            setModalContent(
              <DeckView
                title={`Trade Row Deck (${game.deck.length})`}
                cards={game.deck}
              />,
            );
          }}
          onTributeDeckClick={() => {
            setModalContent(
              <TributeDeckView
                title={`Tribute Deck (${game.tributeDeck.length})`}
                cards={game.tributeDeck}
              />,
            );
          }}
          currentTutorialMessage={currentTutorialMessage}
        />
      </>
    );
  }

  const tutorialView =
    actionStore && !animatingKeyRef.current ? (
      <TutorialMessage actionStore={actionStore} />
    ) : null;

  let containerStyles: {
    favor?: SerializedStyles;
    resources?: SerializedStyles;
    typeCounts?: SerializedStyles;
  } = {};
  if (
    currentTutorialMessage?.highlightType ===
    TutorialHighlightType.PLAYER_SUMMARY
  ) {
    currentTutorialMessage.highlightedElements.forEach((e) => {
      match(e)
        .with(TutorialHighlightElementPlayerSummary.PLAYER_FAVOR, () => {
          containerStyles.favor = PlayerSummarySectionHighlightStyle;
        })
        .with(TutorialHighlightElementPlayerSummary.PLAYER_RESOURCES, () => {
          containerStyles.resources = PlayerSummarySectionHighlightStyle;
        })
        .with(TutorialHighlightElementPlayerSummary.PLAYER_TYPE_COUNTS, () => {
          containerStyles.typeCounts = PlayerSummarySectionHighlightStyle;
        })
        .otherwise(() => {});
    });
  }
  const tutorialHighlightLogKey =
    currentTutorialMessage?.highlightType === TutorialHighlightType.GAME_LOG
      ? currentTutorialMessage.elementKey
      : undefined;

  return (
    <div css={GameStyles.container}>
      <div css={GameStyles.leftPane}>
        {game_content}
        <div css={GameStyles.playersContainer}>
          {sortedPlayers.map((player) => (
            <PlayerBoardView
              key={player.userID}
              player={player}
              game={game}
              user={userByID.get(player.userID)!}
              ref={(node) =>
                animationRefSetter(
                  player.userID,
                  PlayerAnimationRefs.board,
                  node,
                )
              }
            />
          ))}
        </div>
      </div>
      <div css={GameStyles.rightPane}>
        <PlayerResourceViews
          sessionPlayerID={sessionUserID}
          game={game}
          players={game.playerStates}
          userByID={userByID}
          readyByUserID={game.readyByUserID}
          animationRefSetter={animationRefSetter}
          containerStyles={containerStyles}
        />
        <LogView2
          game={game}
          userByID={userByID}
          highlightedEntryKey={tutorialHighlightLogKey}
        />
        <CollapsibleGameLinksView />
      </div>
      <Modal show={!!modalContent} onHide={() => setModalContent(null)}>
        {modalContent ?? undefined}
      </Modal>
      {HoverCardStore.getHoverCardComponent()}
      <AnimationLayer />
      {tutorialView}
    </div>
  );
}

const GameStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'relative',
    '--right-pane-width': `325px`,
    '--mid-pane-margin': '5px',
    [MEDIA_QUERY_MOBILE]: {
      '--right-pane-width': '275px',
      '--mid-pane-margin': '0px',
      overflowX: 'hidden',
    },
  }),
  leftPane: css({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginRight: 'calc(var(--right-pane-width) + var(--mid-pane-margin))',
  }),
  rightPane: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    width: 'var(--right-pane-width)',
    minWidth: 'var(--right-pane-width)',
    height: ['100vh', '100dvh'],
    position: 'fixed',
    top: 0,
    bottom: 0,
    right: 5,
    boxSizing: 'border-box',
    paddingTop: 5,
    paddingBottom: 5,
  }),
  playersContainer: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(326px, 1fr))',
    gap: 5,
    margin: 5,
  }),
  rightPaneBuffer: css({
    flexGrow: 1,
  }),
  cardOverlay: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    position: 'absolute',
    top: 0,
    left: 0,
    overflow: 'hidden',

    width: '100%',
    height: '100%',

    borderRadius: CARD_BORDER_RADIUS,
  }),
  unaffordableCardText: css({
    fontSize: 14,
    textAlign: 'center',
    color: UIColorPalette.unaffordableCardText,
    textShadow: '0px 1px rgba(0, 0, 0, 0.8)',
    backgroundColor: UIColorPalette.unaffordableCardBannerBG,
    width: '120%',
    padding: 4,
    transform: 'rotate(-15deg)',
  }),
  tributeRowTitle: css({
    color: UIColorPalette.gamePileTitleText,
    fontSize: 12,
    display: 'flex',
    flexDirection: 'row',
    gap: 2,
  }),
  tributeTitleSymbol: css({
    height: 12,
    width: 'auto',
    filter: 'invert(100%)',
  }),
};
